import { MotionValue, useMotionValueEvent, useScroll, useSpring, useTransform } from "motion/react";
import { createContext, ReactNode, useContext, useRef, useState, type MutableRefObject } from "react";

type HeroScrollContextValue = {
  heroRef: MutableRefObject<HTMLElement | null>;
  heroProgress: MotionValue<number>;
  headerY: MotionValue<number>;
  headerOpacity: MotionValue<number>;
  brandProgress: MotionValue<number>;
  railsFrozen: boolean;
  railsFrozenAtIndex: number | null;
  setRailsFrozenOverride: (frozen: boolean, atIndex?: number | null) => void;
};

const HeroScrollContext = createContext<HeroScrollContextValue | null>(null);

export function HeroScrollProvider({ children }: { children: ReactNode }) {
  const heroRef = useRef<HTMLElement | null>(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start end", "end start"],
  });

  // Smooth hero progress to reduce micro-jitter during touch scroll
  const heroProgressSmooth = useSpring(heroProgress, { stiffness: 120, damping: 26, mass: 0.8 });
  const headerY = useTransform(heroProgressSmooth, [0, 0.5], [32, 0]);
  const headerOpacity = useTransform(heroProgressSmooth, [0.05, 0.5], [0, 1]);
  const brandProgress = useTransform(heroProgressSmooth, [0, 0.5], [0, 1]);

  const [railsFrozen, setRailsFrozen] = useState(false);
  const [railsFrozenOverrideState, setRailsFrozenOverrideState] = useState(false);
  const [railsFrozenAtIndex, setRailsFrozenAtIndex] = useState<number | null>(null);
  useMotionValueEvent(heroProgressSmooth, "change", () => {
    // Rails freeze is controlled only by explicit overrides (e.g., Manifesto)
    setRailsFrozen(railsFrozenOverrideState);
  });

  const value: HeroScrollContextValue = {
    heroRef,
    heroProgress,
    headerY,
    headerOpacity,
    brandProgress,
    railsFrozen,
    railsFrozenAtIndex,
    setRailsFrozenOverride: (frozen: boolean, atIndex?: number | null) => {
      setRailsFrozenOverrideState(frozen);
      setRailsFrozenAtIndex(atIndex ?? null);
      // Keep railsFrozen coherent immediately based solely on override
      setRailsFrozen(frozen);
    },
  };

  return <HeroScrollContext.Provider value={value}>{children}</HeroScrollContext.Provider>;
}

export function useHeroScroll() {
  const context = useContext(HeroScrollContext);

  if (!context) {
    throw new Error("useHeroScroll must be used within a HeroScrollProvider");
  }

  return context;
}
